import React, { FC, ReactElement, useState } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Checkbox, FormControlLabel } from '@mui/material';
import Card from '@app/components/card/Card';
import CardHeader from '@app/components/card/CardHeader';
import Button from '@app/components/Button';
import { RowSeparator } from '@app/components/Styles';
import InputBox from '@app/components/inputs/inputBox/InputBox';
import NakedDropdown from '@app/components/inputs/NakedDropdown';
import DropdownItem, { DropdownItemProps } from '@app/components/inputs/DropdownItem';
import { BodyRegularStartTransparentBlack600, SubtitleRegularStartTransparentBlack900 } from '@app/components/Text';
import useAppStores from '@app/hooks/useAppStores';
import useInfraStores from '@app/hooks/useInfraStores';
import MorteeMode from '@mortee/morteeMode';
import { ValidatedPayeeWithAccounts } from '@app/domain/validatedPayeeWithAccounts';
import { ValidatedPayeeAccount } from '@app/domain/validatedPayeeAccount';
import { formatAccountWithSwiftban } from '@app/domain/accountDetailsFormatters';
import { ValidationSystemOrganization } from '@mortee/domain/validationSystem';
import { canDuplicateValidatedPayeeAccountToOrganization } from '@mortee/domain/vaildatedPayeeManagement';
import ValidatedPayeeManagementServices from '@mortee/services/validatedPayeeManagementServices';
import { isTruthy } from '@app/utils/utils';
import { pluralize } from '@app/utils/stringUtils';
import { shoot } from '@app/utils/messageLauncher';
import browserHistory from '@app/utils/browserHistory';

interface Props {
  validatedPayeeWithAccounts: ValidatedPayeeWithAccounts;
  onDone: () => void;
}

function getOrganizationDisplayName(organization: ValidationSystemOrganization): string {
  return [organization.groupName, organization.name].filter(isTruthy).join(' - ');
}

const DuplicateValidatedPayeeToOrganizationModal: FC<Props> = observer(({ validatedPayeeWithAccounts, onDone }) => {
  const { payee, accounts, aggregatedPayeeData } = validatedPayeeWithAccounts;
  const { validationSystemStore } = useAppStores<MorteeMode>();
  const { navigationStore } = useInfraStores<MorteeMode>();

  const duplicableAccounts = accounts.filter(canDuplicateValidatedPayeeAccountToOrganization);
  const nonDuplicableAccountsCount = accounts.length - duplicableAccounts.length;
  const sourceOrganizationId = payee.events[0]?.contributingOrganizationId;

  const [targetOrganizationId, setTargetOrganizationId] = useState<string | undefined>(undefined);
  const [selectedAccountUniformIds, setSelectedAccountUniformIds] = useState<string[]>(() =>
    duplicableAccounts.map((account) => account.uniformId),
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const targetOrganizations = validationSystemStore.organizations.map((organizations) =>
    organizations.filter((organization) => organization.id !== sourceOrganizationId),
  );
  const targetOrganization = targetOrganizations.result?.find((organization) => organization.id === targetOrganizationId);

  const areAllAccountsSelected = selectedAccountUniformIds.length === duplicableAccounts.length;
  const canDuplicate = !!targetOrganization && selectedAccountUniformIds.length > 0;

  const handleAccountCheckChanged = (accountUniformId: string, isChecked: boolean): void => {
    setSelectedAccountUniformIds((currentIds) =>
      isChecked ? [...currentIds, accountUniformId] : currentIds.filter((id) => id !== accountUniformId),
    );
  };

  const handleSelectAllChanged = (): void => {
    setSelectedAccountUniformIds(areAllAccountsSelected ? [] : duplicableAccounts.map((account) => account.uniformId));
  };

  const handleDuplicate = async (): Promise<void> => {
    if (!targetOrganization) {
      return;
    }

    setIsSaving(true);

    try {
      const duplicatedPayeeWithAccounts = await ValidatedPayeeManagementServices.adminCopyValidatedPayeeToOrganization(
        payee.uniformId,
        {
          targetOrganizationId: targetOrganization.id,
          accountUniformIds: duplicableAccounts
            .map((account) => account.uniformId)
            .filter((uniformId) => selectedAccountUniformIds.includes(uniformId)),
        },
      );

      onDone();
      browserHistory.push(navigationStore.generateManageValidatedPayeePageHref(duplicatedPayeeWithAccounts.payee.uniformId));
      shoot(
        { type: 'success', closeable: true },
        `Validated payee duplicated to ${getOrganizationDisplayName(targetOrganization)}`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const renderOrganizationItem = (organization: ValidationSystemOrganization): ReactElement<DropdownItemProps> => {
    const displayName = getOrganizationDisplayName(organization);

    return (
      <DropdownItem key={organization.id} value={organization.id} textWhenSelected={displayName}>
        {displayName}
      </DropdownItem>
    );
  };

  const renderAccountLabel = (account: ValidatedPayeeAccount): ReactElement => {
    const formattedAccounts = formatAccountWithSwiftban({
      ...account.accountDetails,
      accountNumber: account.presentationAccountNumber || account.accountDetails.accountNumber,
      localFormat: account.localFormat ?? null,
    });

    return (
      <CheckboxLabel>
        <BankName>{account.bankName ?? 'Unknown bank'}</BankName>
        {formattedAccounts.map((formattedAccount) => (
          <div key={formattedAccount}>{formattedAccount}</div>
        ))}
      </CheckboxLabel>
    );
  };

  return (
    <MainCard shadowType='elevated' dataTestId='duplicate-validated-payee-to-organization-modal'>
      <CardHeader>DUPLICATE TO OTHER ORGANIZATION</CardHeader>
      <Content>
        <Description>
          Duplicate {aggregatedPayeeData.mainName ?? 'this payee'} and the selected accounts to another organization, with
          organizational visibility.
        </Description>
        <SectionTitle>Target Organization</SectionTitle>
        <InputBox>
          <NakedDropdown
            accessibilityLabel='target organization'
            name='drp-duplicate-validated-payee-target-organization'
            dataTestId='drp-duplicate-validated-payee-target-organization'
            placeholder='Organization'
            value={targetOrganizationId}
            onChange={(organizationId: string | undefined): void => setTargetOrganizationId(organizationId)}
            loading={targetOrganizations.isNotSettled()}
            disabled={isSaving}
            isSearchable
          >
            {(targetOrganizations.result ?? []).map(renderOrganizationItem)}
          </NakedDropdown>
        </InputBox>
        <SectionTitle>Accounts</SectionTitle>
        {duplicableAccounts.length > 1 && (
          <>
            <StyledFormControlLabel
              control={
                <StyledCheckbox
                  id='cbox-duplicate-validated-payee-accounts-select-all'
                  data-testid='cbox-duplicate-validated-payee-accounts-select-all'
                  indeterminate={selectedAccountUniformIds.length > 0 && !areAllAccountsSelected}
                  checked={areAllAccountsSelected}
                  onChange={handleSelectAllChanged}
                  disabled={isSaving}
                  color='primary'
                />
              }
              label={<CheckboxLabel>Select All</CheckboxLabel>}
            />
            <AccountsSeparator light />
          </>
        )}
        <CheckboxList>
          {duplicableAccounts.map((account) => (
            <StyledFormControlLabel
              key={account.uniformId}
              control={
                <StyledCheckbox
                  id={`cbox-duplicate-validated-payee-account-${account.uniformId}`}
                  data-testid={`cbox-duplicate-validated-payee-account-${account.uniformId}`}
                  checked={selectedAccountUniformIds.includes(account.uniformId)}
                  onChange={(event): void => handleAccountCheckChanged(account.uniformId, event.currentTarget.checked)}
                  disabled={isSaving}
                  color='primary'
                />
              }
              label={renderAccountLabel(account)}
            />
          ))}
        </CheckboxList>
        {nonDuplicableAccountsCount > 0 && (
          <Note data-testid='duplicate-validated-payee-non-duplicable-accounts-note'>
            {nonDuplicableAccountsCount} {pluralize('account', nonDuplicableAccountsCount)} not listed. Only accounts with
            organizational visibility in one organization can be duplicated.
          </Note>
        )}
      </Content>
      <ActionsContainer>
        <Button
          id='btn-duplicate-validated-payee-to-organization-cancel'
          appearance='text'
          colorScheme='secondary'
          disabled={isSaving}
          onClick={onDone}
        >
          CANCEL
        </Button>
        <Button
          id='btn-duplicate-validated-payee-to-organization-confirm'
          appearance='full'
          colorScheme='secondary'
          disabled={!canDuplicate}
          loading={isSaving}
          onClick={handleDuplicate}
        >
          DUPLICATE
        </Button>
      </ActionsContainer>
    </MainCard>
  );
});

export default DuplicateValidatedPayeeToOrganizationModal;

const MainCard = styled(Card)`
  width: min(600px, 100vw);

  // Padding will be applied by the content
  padding: 0;

  --card-side-padding: 24px;
  --card-padding: 16px var(--card-side-padding);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 var(--card-side-padding);
`;

const Description = styled(BodyRegularStartTransparentBlack600.div)`
  margin-bottom: 8px;
`;

const SectionTitle = styled(SubtitleRegularStartTransparentBlack900.div)`
  margin: 16px 0 8px;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 300px;
  overflow-y: auto;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-left: 0;
  align-items: flex-start;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-left: -12px;
`;

// Matches the checkbox's own padding, since the labels are top-aligned to fit multi-line accounts.
const CheckboxLabel = styled.div`
  padding-top: 9px;
`;

const BankName = styled.div`
  font-weight: bold;
`;

const AccountsSeparator = styled(RowSeparator)`
  margin-bottom: 8px;
`;

const Note = styled(BodyRegularStartTransparentBlack600.div)`
  margin-top: 8px;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  // To allow the buttons drop shadow to fully show
  padding: var(--card-padding);

  & > *:not(:first-child) {
    margin-left: 8px;
  }
`;
